import { useState, useMemo, useEffect } from 'react'
import { useBreadcrumb } from '../hooks/useBreadcrumb'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { FaCalculator, FaPlane, FaShip, FaTruck, FaSearch, FaDollarSign, FaRoute, FaExchangeAlt, FaMapMarkerAlt, FaCheckCircle, FaBox, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa'
import { bookingService } from '../services'
import { Card, Button, Input, Select, PageHeader, LoadingSpinner, Modal } from '../components/common'

// 比较预估 - 运货方式
const compareShippingMethods = [
  { value: '__ALL__', label: '全部方式' },
  { value: '国际空派', label: '国际空派' },
  { value: '国际海派', label: '国际海派' },
  { value: '国际快递', label: '国际快递' },
  { value: '国际小包', label: '国际小包' },
]

// 精准预估 - 运货方式
const preciseShippingMethods = [
  { value: '__ALL__', label: '全部方式' },
  { value: '国际空派', label: '国际空派' },
  { value: '国际海派', label: '国际海派' },
  { value: '国际快递', label: '国际快递' },
  { value: '国际小包', label: '国际小包' },
]

const ALL_METHOD_VALUE = '__ALL__'

const yesNoOptions = [
  { value: '1', label: '是' },
  { value: '0', label: '否' },
]

const methodIcons = {
  '国际空派': FaPlane,
  '国际海派': FaShip,
  '国际小包': FaBox,
  '国际快递': FaTruck,
}

const ESTIMATE_MODES = {
  COMPARE: 'compare',
  PRECISE: 'precise',
}

export default function FreightCalculatorPage() {
  const breadcrumb = useBreadcrumb()
  const [mode, setMode] = useState(ESTIMATE_MODES.COMPARE)
  const [results, setResults] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  // 国家列表（从API获取）
  const [countries, setCountries] = useState([])

  // ===== 精准预估 额外状态 =====
  const [itemTypeNameMap, setItemTypeNameMap] = useState({})
  const [itemTypeOptions, setItemTypeOptions] = useState([])
  const [warehouseAddresses, setWarehouseAddresses] = useState([])
  const [showElectromagneticModal, setShowElectromagneticModal] = useState(false)
  const [showSensitiveModal, setShowSensitiveModal] = useState(false)

  // 比较预估表单
  const compareForm = useForm({
    defaultValues: {
      hasElectromagnetic: '',
      weight: '',
      length: '',
      width: '',
      height: '',
      destinationCountry: '',
      shippingMethod: '',
    }
  })

  // 精准预估表单
  const preciseForm = useForm({
    defaultValues: {
      originCountry: '中国',
      pickupRequired: '0',
      pickupProvince: '',
      pickupCity: '',
      pickupDistrict: '',
      pickupStreet: '',
      pickupDetailAddress: '',
      destinationCountry: '',
      destinationCity: '',
      detailAddress: '',
      itemType: '',
      itemName: '',
      weight: '',
      length: '',
      width: '',
      height: '',
      itemValue: '',
      shippingMethod: '',
      warehouseAddress: '',
      needDelivery: '',
    }
  })

  /*
   * RHF 的 setValue 是稳定引用，抽出来当依赖：既满足 exhaustive-deps，
   * 又不会像把整个 preciseForm 塞进依赖那样让 effect 反复执行。
   */
  const setPreciseValue = preciseForm.setValue

  // ===== 加载国家列表 =====
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const res = await bookingService.getDestinationCountries()
        const list = Array.isArray(res) ? res : (res?.data || [])
        const mapped = list.map(c => {
          if (typeof c === 'string') return { value: c, label: c }
          const name = c.countryNameEn
            ? `${c.countryName} / ${c.countryNameEn}`
            : c.countryName
          return { value: name, label: name }
        })
        setCountries(mapped)
      } catch (e) {
        console.error('加载国家列表失败', e)
      }
    }
    loadCountries()
  }, [])

  // ===== 比较预估 - 含电含磁联动敏感货 =====
  const watchElectromagnetic = compareForm.watch('hasElectromagnetic')
  const isSensitiveAuto = watchElectromagnetic === '1' ? '是' : watchElectromagnetic === '0' ? '否' : ''

  // ===== 比较预估 - 体积比自动计算 =====
  const compareLength = compareForm.watch('length')
  const compareWidth = compareForm.watch('width')
  const compareHeight = compareForm.watch('height')
  const compareVolume = useMemo(() => {
    const l = parseFloat(compareLength)
    const w = parseFloat(compareWidth)
    const h = parseFloat(compareHeight)
    if (l > 0 && w > 0 && h > 0) {
      return (l * w * h / 1000000).toFixed(4)
    }
    return null
  }, [compareHeight, compareLength, compareWidth])
  const volumeRatio = useMemo(() => {
    const l = parseFloat(compareLength)
    const w = parseFloat(compareWidth)
    const h = parseFloat(compareHeight)
    if (l > 0 && w > 0 && h > 0) {
      return (l * w * h / 6000).toFixed(4)
    }
    return null
  }, [compareLength, compareWidth, compareHeight])

  // ===== 精准预估 - 取货地址级联 =====
  const watchPickupRequired = preciseForm.watch('pickupRequired')
  const preciseLength = preciseForm.watch('length')
  const preciseWidth = preciseForm.watch('width')
  const preciseHeight = preciseForm.watch('height')
  const preciseVolume = useMemo(() => {
    const l = parseFloat(preciseLength)
    const w = parseFloat(preciseWidth)
    const h = parseFloat(preciseHeight)
    if (l > 0 && w > 0 && h > 0) {
      return (l * w * h / 1000000).toFixed(4)
    }
    return null
  }, [preciseHeight, preciseLength, preciseWidth])
  const preciseVolumeRatio = useMemo(() => {
    const l = parseFloat(preciseLength)
    const w = parseFloat(preciseWidth)
    const h = parseFloat(preciseHeight)
    if (l > 0 && w > 0 && h > 0) {
      return (l * w * h / 6000).toFixed(4)
    }
    return null
  }, [preciseHeight, preciseLength, preciseWidth])

  // ===== 精准预估 - 目的国家改变 → 加载物品类型和仓库 =====
  const watchDestCountry = preciseForm.watch('destinationCountry')

  useEffect(() => {
    if (!watchDestCountry) {
      setItemTypeOptions([])
      setItemTypeNameMap({})
      setWarehouseAddresses([])
      setPreciseValue('itemType', '')
      setPreciseValue('itemName', '')
      setPreciseValue('warehouseAddress', '')
      return
    }
    // 加载敏感货选项（含物品类型/名称）
    const loadOptions = async () => {
      try {
        const res = await bookingService.getSensitiveGoodsOptions(watchDestCountry)
        const data = res?.data || res || {}
        const typeNameMap = data.itemTypeNames || {}
        setItemTypeNameMap(typeNameMap)
        setItemTypeOptions(Object.keys(typeNameMap).map(name => ({ value: name, label: name })))
      } catch (e) { setItemTypeOptions([]); setItemTypeNameMap({}) }
    }
    loadOptions()

    // 加载仓库地址
    const loadWarehouses = async () => {
      try {
        const res = await bookingService.getWarehouseAddresses(watchDestCountry)
        const data = Array.isArray(res) ? res : (res?.data || [])
        setWarehouseAddresses(data)
        // 若只有一个，自动选择
        if (data.length === 1) {
          setPreciseValue('warehouseAddress', String(data[0].id))
        } else {
          setPreciseValue('warehouseAddress', '')
        }
      } catch (e) { setWarehouseAddresses([]) }
    }
    loadWarehouses()
  }, [watchDestCountry, setPreciseValue])

  // ===== 精准预估 - 物品类型改变 → 过滤物品名称 =====
  const watchItemType = preciseForm.watch('itemType')
  const filteredItemNames = useMemo(() => {
    if (!watchItemType) return []
    return (itemTypeNameMap[watchItemType] || []).map(name => ({ value: name, label: name }))
  }, [watchItemType, itemTypeNameMap])

  // ===== 精准预估 - 运货方式改变 → 本地派送联动 =====
  const watchShippingMethod = preciseForm.watch('shippingMethod')

  useEffect(() => {
    if (watchShippingMethod === '国际空派' || watchShippingMethod === '国际海派') {
      setPreciseValue('needDelivery', '0')
    }
  }, [watchShippingMethod, setPreciseValue])

  const deliveryDisabled = watchShippingMethod === '国际空派' || watchShippingMethod === '国际海派'

  const getDeliveryTip = () => {
    if (watchShippingMethod === '国际空派' || watchShippingMethod === '国际海派') {
      return '若需要派送请下单时备注'
    }
    if (watchShippingMethod === ALL_METHOD_VALUE) {
      return '仅支持国际快递、国际小包派送，其余需到仓库地址自提，若需要派送请下单时备注'
    }
    return null
  }

  // ===== 比较预估提交 =====
  const onCompareSubmit = async (data) => {
    // 含电含磁 → 弹窗提示
    if (data.hasElectromagnetic === '1') {
      setShowElectromagneticModal(true)
      return
    }

    setIsLoading(true)
    try {
      const res = await bookingService.getCompareEstimate({
        country: data.destinationCountry,
        hasElectromagnetic: parseInt(data.hasElectromagnetic),
        weight: parseFloat(data.weight),
        shippingMethod: data.shippingMethod === ALL_METHOD_VALUE ? '' : (data.shippingMethod || ''),
      })
      const list = Array.isArray(res) ? res : (res?.data || [])
      setResults({ type: 'compare', data: list })
    } catch (error) {
      toast.error(error.message || '获取报价失败')
    } finally {
      setIsLoading(false)
    }
  }

  // 含电含磁确认
  const handleElectromagneticConfirm = () => {
    setShowElectromagneticModal(false)
    setResults({ type: 'compare', data: null, noQuote: true })
  }

  // ===== 精准预估提交 =====
  const onPreciseSubmit = async (data) => {
    setIsLoading(true)
    try {
      const volume = (data.length && data.width && data.height)
        ? (parseFloat(data.length) * parseFloat(data.width) * parseFloat(data.height) / 1000000)
        : null

      const res = await bookingService.getPreciseQuote({
        destinationCountry: data.destinationCountry,
        itemType: data.itemType,
        itemName: data.itemName,
        weight: parseFloat(data.weight),
        volume: volume,
        shippingMethod: data.shippingMethod === ALL_METHOD_VALUE ? '' : (data.shippingMethod || ''),
      })

      const result = res?.data || res || {}

      // 敏感货检查
      if (result.isSensitive) {
        setIsLoading(false)
        setShowSensitiveModal(true)
        return
      }

      setResults({ type: 'precise', data: result })
    } catch (error) {
      toast.error(error.message || '获取报价失败')
    } finally {
      setIsLoading(false)
    }
  }

  // 敏感货确认
  const handleSensitiveConfirm = () => {
    setShowSensitiveModal(false)
    setResults({ type: 'precise', data: null, noQuote: true })
  }

  // 切换模式
  const handleModeChange = (newMode) => {
    setMode(newMode)
    setResults(null)
  }

  // ===== 渲染比较预估表单 =====
  const renderCompareForm = () => {
    const { register, handleSubmit, formState: { errors } } = compareForm

    return (
      <form onSubmit={handleSubmit(onCompareSubmit)} className="space-y-6">
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          <Select
            label="是否含电含磁物品"
            required
            placeholder="请选择"
            options={yesNoOptions}
            error={errors.hasElectromagnetic?.message}
            {...register('hasElectromagnetic', { required: '请选择是否含电含磁' })}
          />

          <div>
          <label className="block mb-1.5 font-medium text-gray-700 text-sm">
            是否为敏感货物 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={isSensitiveAuto}
            disabled
            className="w-full px-4 py-2.5 border border-gray-300 rounded-md bg-gray-100 text-gray-500"
            placeholder="根据含电含磁自动判断"
          />
          </div>

          <Input
            label="物品重量 (KG)"
            type="number"
            step="0.01"
            placeholder="请输入重量"
            required
            error={errors.weight?.message}
            {...register('weight', {
              required: '请输入重量',
              min: { value: 0.01, message: '重量必须大于0' },
            })}
          />

          <div className="md:col-span-2 xl:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              物品体积 (长 × 宽 × 高 CM) <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              <Input
                type="number" step="0.1" placeholder="长"
                error={errors.length?.message}
                {...register('length', {
                  required: '请输入长度',
                  min: { value: 0.01, message: '长度必须大于0' },
                })}
              />
              <Input
                type="number" step="0.1" placeholder="宽"
                error={errors.width?.message}
                {...register('width', {
                  required: '请输入宽度',
                  min: { value: 0.01, message: '宽度必须大于0' },
                })}
              />
              <Input
                type="number" step="0.1" placeholder="高"
                error={errors.height?.message}
                {...register('height', {
                  required: '请输入高度',
                  min: { value: 0.01, message: '高度必须大于0' },
                })}
              />
            </div>
          </div>

          <div>
          <label className="block mb-1.5 font-medium text-gray-700 text-sm">
            体积
          </label>
          <input
            type="text"
            value={compareVolume ? `${compareVolume} CBM` : '长 × 宽 × 高 / 1000000'}
            disabled
            className="w-full px-4 py-2.5 border border-gray-300 rounded-md bg-gray-100 text-gray-500"
          />
          {compareVolume && (
            <p className="text-xs text-gray-400 mt-1">计算公式: 长 × 宽 × 高 / 1000000</p>
          )}
          </div>

          <div>
          <label className="block mb-1.5 font-medium text-gray-700 text-sm">
            体积比
          </label>
          <input
            type="text"
            value={volumeRatio || '长 × 宽 × 高 / 6000'}
            disabled
            className="w-full px-4 py-2.5 border border-gray-300 rounded-md bg-gray-100 text-gray-500"
          />
          {volumeRatio && (
            <p className="text-xs text-gray-400 mt-1">计算公式: 长 × 宽 × 高 / 6000</p>
          )}
          </div>

          <Select
            label="海外目的地"
            required
            placeholder="请选择目的国家"
            options={countries}
            error={errors.destinationCountry?.message}
            {...register('destinationCountry', { required: '请选择目的国家' })}
          />

          <Select
            label="货运方式"
            required
            placeholder="请选择货运方式"
            options={compareShippingMethods}
            error={errors.shippingMethod?.message}
            {...register('shippingMethod', { required: '请选择货运方式' })}
          />
        </div>

        <Button type="submit" loading={isLoading} className="w-full" icon={<FaCalculator />}>
          计算运费
        </Button>
      </form>
    )
  }

  // ===== 渲染精准预估表单 =====
  const renderPreciseForm = () => {
    const { register, handleSubmit, formState: { errors } } = preciseForm


    return (
      <form onSubmit={handleSubmit(onPreciseSubmit)} className="space-y-6">
        <div className="space-y-2">
          {Number(watchPickupRequired) === 1 && (
            <div className="hidden xl:grid xl:grid-cols-3 xl:gap-4">
              <div></div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2">
                <FaInfoCircle className="text-yellow-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-yellow-700">上门取货属于额外费用，具体费用请联系客服。</p>
              </div>
              <div></div>
            </div>
          )}

          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
            {/* 发货国家 */}
            <div>
              <label className="block mb-1.5 font-medium text-gray-700 text-sm">
                发货国家 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value="中国"
                disabled
                className="w-full px-4 py-2.5 border border-gray-300 rounded-md bg-gray-100 text-gray-500"
              />
            </div>

            {/* 是否需要上门取货 */}
            <div>
              {/* <div className="xl:hidden mb-2">
                {Number(watchPickupRequired) === 1 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2">
                    <FaInfoCircle className="text-yellow-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-yellow-700">上门取货属于额外费用，具体费用请联系客服。</p>
                  </div>
                )}
              </div> */}
              <Select
                label="是否需要上门取货"
                required
                placeholder="请选择"
                options={yesNoOptions}
                error={errors.pickupRequired?.message}
                {...register('pickupRequired', { required: '请选择是否需要上门取货' })}
              />
            </div>

            <Select
              label="目的地国家"
              required
              placeholder="请选择目的地国家"
              options={countries}
              error={errors.destinationCountry?.message}
              {...register('destinationCountry', { required: '请选择目的地国家' })}
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          <Input
            label="目的地城市"
            placeholder="请输入目的地城市"
            required
            error={errors.destinationCity?.message}
            {...register('destinationCity', { required: '请输入目的地城市' })}
          />

          <Input
            label="详细地址"
            placeholder="请输入详细收货地址"
            required
            error={errors.detailAddress?.message}
            {...register('detailAddress', { required: '请输入详细地址' })}
          />

          <Select
            label="物品类型"
            required
            placeholder={!watchDestCountry ? '请先选择目的地国家' : '请选择物品类型'}
            options={itemTypeOptions}
            disabled={!watchDestCountry}
            error={errors.itemType?.message}
            {...register('itemType', { required: '请选择物品类型' })}
          />

          <Select
            label="物品名称"
            required
            placeholder={!watchItemType ? '请先选择物品类型' : '请选择物品名称'}
            options={filteredItemNames}
            disabled={!watchItemType}
            error={errors.itemName?.message}
            {...register('itemName', { required: '请选择物品名称' })}
          />

          <Input
            label="物品重量 (KG)"
            type="number"
            step="0.01"
            placeholder="请输入重量"
            required
            error={errors.weight?.message}
            {...register('weight', {
              required: '请输入重量',
              min: { value: 0.01, message: '重量必须大于0' },
            })}
          />

          <div className="md:col-span-2 xl:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              物品体积 (长 × 宽 × 高 CM) <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              <Input
                type="number"
                step="0.1"
                placeholder="长"
                error={errors.length?.message}
                {...register('length', {
                  required: '请输入长度',
                  min: { value: 0.01, message: '长度必须大于0' },
                })}
              />
              <Input
                type="number"
                step="0.1"
                placeholder="宽"
                error={errors.width?.message}
                {...register('width', {
                  required: '请输入宽度',
                  min: { value: 0.01, message: '宽度必须大于0' },
                })}
              />
              <Input
                type="number"
                step="0.1"
                placeholder="高"
                error={errors.height?.message}
                {...register('height', {
                  required: '请输入高度',
                  min: { value: 0.01, message: '高度必须大于0' },
                })}
              />
            </div>
          </div>

          <div>
            <label className="block mb-1.5 font-medium text-gray-700 text-sm">
              体积
            </label>
            <input
              type="text"
              value={preciseVolume ? `${preciseVolume} CBM` : '长 × 宽 × 高 / 1000000'}
              disabled
              className="w-full px-4 py-2.5 border border-gray-300 rounded-md bg-gray-100 text-gray-500"
            />
            {preciseVolume && (
              <p className="text-xs text-gray-400 mt-1">计算公式: 长 × 宽 × 高 / 1000000</p>
            )}
          </div>

          <div>
            <label className="block mb-1.5 font-medium text-gray-700 text-sm">
              体积比
            </label>
            <input
              type="text"
              value={preciseVolumeRatio || '长 × 宽 × 高 / 6000'}
              disabled
              className="w-full px-4 py-2.5 border border-gray-300 rounded-md bg-gray-100 text-gray-500"
            />
            {preciseVolumeRatio && (
              <p className="text-xs text-gray-400 mt-1">计算公式: 长 × 宽 × 高 / 6000</p>
            )}
          </div>

          <Input
            label="物品价值"
            type="number"
            step="0.01"
            placeholder="请输入物品价值"
            error={errors.itemValue?.message}
            {...register('itemValue', {
              validate: value => (!value || parseFloat(value) > 0) || '物品价值必须大于0',
            })}
          />

          <Select
            label="选择运货方式"
            required
            placeholder="请选择运货方式"
            options={preciseShippingMethods}
            error={errors.shippingMethod?.message}
            {...register('shippingMethod', { required: '请选择运货方式' })}
          />

          <div>
            <Select
              label="目的地国家仓库地址"
              placeholder={warehouseAddresses.length === 0 ? '暂无仓库地址' : '请选择仓库地址'}
              options={warehouseAddresses.map(w => ({ value: String(w.id), label: w.address }))}
              disabled={warehouseAddresses.length === 0}
              {...register('warehouseAddress')}
            />
            {warehouseAddresses.length === 1 && (
              <p className="text-xs text-gray-400 mt-1">已自动选择唯一仓库地址</p>
            )}
          </div>

          <div>
            <Select
              label="是否需要本地派送"
              required
              placeholder="请选择"
              options={yesNoOptions}
              disabled={deliveryDisabled}
              error={errors.needDelivery?.message}
              {...register('needDelivery', { required: '请选择是否需要本地派送' })}
            />
            {getDeliveryTip() && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mt-2 flex items-start gap-2">
                <FaInfoCircle className="text-blue-500 mt-0.5 flex-shrink-0 text-xs" />
                <p className="text-xs text-blue-700">{getDeliveryTip()}</p>
              </div>
            )}
          </div>
        </div>


        <Button type="submit" loading={isLoading} className="w-full" icon={<FaCalculator />}>
          获取精准报价
        </Button>
      </form>
    )
  }

  // ===== 渲染比较预估结果 =====
  const renderCompareResults = () => {
    if (results?.noQuote) {
      return (
        <div className="text-center py-16">
          <FaExclamationTriangle className="text-4xl mx-auto mb-4 text-orange-400" />
          <p className="text-xl font-semibold text-gray-700">暂无报价</p>
          <p className="text-sm text-gray-500 mt-2">含电含磁物品暂不支持运输报价</p>
        </div>
      )
    }

    const quotes = results?.data || []

    if (quotes.length === 0) {
      return (
        <div className="text-center py-16 text-gray-500">
          <p>暂无可用渠道报价</p>
          <p className="text-sm mt-2">请尝试其他目的地或运输方式</p>
        </div>
      )
    }

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-gray-900">
            找到 {quotes.length} 个渠道报价
          </h4>
          <span className="text-sm text-gray-500">按价格从低到高排序</span>
        </div>
        {quotes.map((quote, index) => {
          const MethodIcon = methodIcons[quote.shippingMethod] || FaTruck
          return (
            <div
              key={index}
              className={`border rounded-lg p-5 hover:shadow-md transition-all ${
                index === 0 ? 'border-primary-300 bg-primary-50/50 ring-1 ring-primary-200' : 'border-gray-200 hover:border-primary-300'
              }`}
            >
              {index === 0 && (
                <div className="flex items-center gap-1 text-primary-600 text-sm font-medium mb-3">
                  <FaCheckCircle />
                  <span>推荐选择</span>
                </div>
              )}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center">
                    <MethodIcon className="text-xl" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{quote.shippingMethod}</h4>
                    <p className="text-sm text-gray-500">{quote.country}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-fluid-2xl font-bold text-primary-600">
                    ¥{parseFloat(quote.freight || 0).toFixed(2)}
                  </div>
                  <p className="text-sm text-gray-500">预估运费</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                <div className="flex items-center gap-2 text-sm">
                  <FaDollarSign className="text-gray-400" />
                  <span className="text-gray-500">单价:</span>
                  <span className="font-medium">¥{quote.unitPrice}/KG</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <FaRoute className="text-gray-400" />
                  <span className="text-gray-500">重量:</span>
                  <span className="font-medium">{quote.weight} KG</span>
                </div>
              </div>

              {quote.remark && (
                <p className="mt-3 text-sm text-gray-500 bg-gray-50 p-3 rounded">
                  {quote.remark}
                </p>
              )}
            </div>
          )
        })}

        <div className="bg-gray-50 rounded-lg p-4 mt-4">
          <p className="text-sm text-gray-500">
            <strong>计算公式：</strong>运费 = 物品重量 × 单价
          </p>
        </div>
      </div>
    )
  }

  // ===== 渲染精准预估结果 =====
  const renderPreciseResults = () => {
    if (results?.noQuote) {
      return (
        <div className="text-center py-16">
          <FaExclamationTriangle className="text-4xl mx-auto mb-4 text-orange-400" />
          <p className="text-xl font-semibold text-gray-700">暂无报价</p>
          <p className="text-sm text-gray-500 mt-2">该物品属于敏感货物，暂不支持运输报价</p>
        </div>
      )
    }

    const data = results?.data
    const priceResults = data?.results || []

    if (priceResults.length === 0) {
      return (
        <div className="text-center py-16 text-gray-500">
          <p>暂无报价信息</p>
          <p className="text-sm mt-2">请检查输入信息后重试</p>
        </div>
      )
    }

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-gray-900">
            找到 {priceResults.length} 个渠道精准报价
          </h4>
        </div>
        {priceResults.map((quote, index) => {
          const MethodIcon = methodIcons[quote.shippingMethod] || FaTruck
          return (
            <div key={index} className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-all">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center">
                    <MethodIcon className="text-xl" />
                  </div>
                  <h4 className="font-semibold text-gray-900">{quote.shippingMethod}</h4>
                </div>
                <div className="text-right">
                  <div className="text-fluid-2xl font-bold text-primary-600">
                    ¥{parseFloat(quote.totalFreight || 0).toFixed(2)}
                  </div>
                  <p className="text-sm text-gray-500">预估总费用</p>
                </div>
              </div>

              <div className="space-y-2 pt-4 border-t border-gray-100">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">基础运费 (重量×单价)</span>
                  <span className="font-medium">
                    {quote.baseFreight ? `¥${parseFloat(quote.baseFreight).toFixed(2)}` : '-'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">燃油附加费</span>
                  <span className="font-medium">
                    {quote.fuelSurcharge ? `¥${parseFloat(quote.fuelSurcharge).toFixed(2)}` : '-'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">偏远地区附加费</span>
                  <span className="font-medium">
                    {quote.remoteSurcharge ? `¥${parseFloat(quote.remoteSurcharge).toFixed(2)}` : '-'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">派送费</span>
                  <span className="font-medium">
                    {quote.deliveryFee ? `¥${parseFloat(quote.deliveryFee).toFixed(2)}` : '-'}
                  </span>
                </div>
                <div className="flex justify-between text-sm pt-2 border-t border-gray-100">
                  <span className="text-gray-500">单价</span>
                  <span className="font-medium">¥{quote.unitPrice}/KG</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">计费重量</span>
                  <span className="font-medium">{quote.weight} KG</span>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        icon={FaCalculator}
        title="运费估算"
        subtitle="智能比价，一键获取多渠道报价"
        breadcrumb={breadcrumb}
      />

      <div className="container py-8">
        {/* 模式切换 */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => handleModeChange(ESTIMATE_MODES.COMPARE)}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                mode === ESTIMATE_MODES.COMPARE
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FaExchangeAlt />
              比较预估
            </button>
            <button
              onClick={() => handleModeChange(ESTIMATE_MODES.PRECISE)}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                mode === ESTIMATE_MODES.PRECISE
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FaMapMarkerAlt />
              精准预估
            </button>
          </div>
        </div>

        {/* 模式说明 */}
        <div className="text-center mb-8">
          {mode === ESTIMATE_MODES.COMPARE ? (
            <p className="text-gray-500">
              输入货物基本信息，一键对比<strong className="text-primary-600">国际空派、海派、小包、快递</strong>等多种物流渠道的运费
            </p>
          ) : (
            <p className="text-gray-500">
              填写详细收货地址，选择物流渠道，获取<strong className="text-primary-600">精准到门报价</strong>（含税费及附加费）
            </p>
          )}
        </div>

        <div className="space-y-8">
          <Card>
            <Card.Header>
              <h4 className="font-semibold flex items-center gap-2">
                <FaSearch className="text-primary-500" />
                {mode === ESTIMATE_MODES.COMPARE ? '输入货物信息' : '填写详细信息'}
              </h4>
            </Card.Header>
            <Card.Body>
              {mode === ESTIMATE_MODES.COMPARE ? renderCompareForm() : renderPreciseForm()}
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <h4 className="font-semibold">
                {mode === ESTIMATE_MODES.COMPARE ? '报价比较结果' : '精准报价结果'}
              </h4>
            </Card.Header>
            <Card.Body>
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <LoadingSpinner size="lg" />
                  <p className="mt-4 text-gray-500">正在计算中...</p>
                </div>
              ) : results ? (
                results.type === 'compare' ? renderCompareResults() : renderPreciseResults()
              ) : (
                <div className="text-center py-16 text-gray-400">
                  <FaCalculator className="text-4xl mx-auto mb-4" />
                  <p>
                    {mode === ESTIMATE_MODES.COMPARE
                      ? '填写货物信息并点击计算，即可获取多渠道报价对比'
                      : '填写详细信息并选择渠道，即可获取精准报价'
                    }
                  </p>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* <Card>
            <Card.Body>
              <h5 className="font-semibold mb-3">💡 温馨提示</h5>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• 报价仅供参考，实际费用以入库称重为准</li>
                <li>• 体积重 = 长×宽×高(cm) / 5000</li>
                <li>• 计费重量取实重与体积重中的较大值</li>
                {mode === ESTIMATE_MODES.PRECISE && (
                  <>
                    <li>• 偏远地区可能产生附加费用</li>
                    <li>• 精准报价有效期为24小时</li>
                  </>
                )}
              </ul>
            </Card.Body>
          </Card> */}
        </div>
      </div>

      {/* ===== 含电含磁 / 敏感货提示弹窗（同一份内容，两个触发源） ===== */}
      {[
        { open: showElectromagneticModal, onConfirm: handleElectromagneticConfirm, key: 'electromagnetic' },
        { open: showSensitiveModal, onConfirm: handleSensitiveConfirm, key: 'sensitive' },
      ].map(({ open, onConfirm, key }) => (
        <Modal key={key} open={open} onClose={onConfirm} closable={false}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 shrink-0 rounded-full bg-orange-100 flex items-center justify-center">
              <FaExclamationTriangle className="text-orange-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">温馨提示</h3>
          </div>
          <p className="text-gray-600 mb-6">
            此物品不能发送，若需要发送请在报名或下单时备注。
          </p>
          <button
            onClick={onConfirm}
            className="w-full sm:w-auto sm:ml-auto sm:block px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
          >
            确认
          </button>
        </Modal>
      ))}
    </div>
  )
}

